import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { useCloseOnEscape } from './topbarChrome'

export interface CartDrawerLabels {
  /** Receives the item count, e.g. `(n) => \`Your Cart (${n})\`` */
  title?: (count: number) => string
  empty?: string
  close?: string
}

export interface CartLine {
  id: string
  name: string
  quantity: number
  price: string
}

/**
 * Anchored under the right end of the pill rather than being a full-height side
 * sheet — the shape wehunt.app uses, which keeps the bar and the cart visually
 * connected. Rendered inside the pill's positioning context, so it needs no
 * backdrop: the caller's outside-click dismisses it.
 *
 * Its z-index sits below the menu/search backdrop on purpose, matching the
 * site: the cart is a dropdown off the bar, not a layer over the page.
 */
export function CartDrawer({
  open,
  onClose,
  lines = [],
  labels,
}: {
  open: boolean
  onClose: () => void
  lines?: CartLine[]
  labels?: CartDrawerLabels
}) {
  useCloseOnEscape(open, onClose)

  if (!open) return null

  const title = labels?.title ? labels.title(lines.length) : `Your Cart (${lines.length})`

  return (
    <div
      role="dialog"
      aria-label={title}
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        width: 'min(380px, calc(100vw - 32px))',
        maxHeight: '70vh',
        zIndex: 150,
        background: COLORS.surfaceContainerLowest,
        color: COLORS.onSurface,
        // Square at the top, where it tucks under the pill.
        borderRadius: '0 0 8px 8px',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: 16,
          flexShrink: 0,
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: 1.5 }}>{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={labels?.close ?? 'Close cart'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            flexShrink: 0,
            border: 'none',
            borderRadius: 6,
            background: 'transparent',
            color: COLORS.onSurfaceVariant,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
        >
          <Icon name="close" size={20} />
        </button>
      </div>

      <div style={{ padding: 16, overflowY: 'auto' }}>
        {lines.length === 0 ? (
          <p
            style={{
              margin: 0,
              padding: '32px 0',
              fontSize: 16,
              textAlign: 'center',
              color: COLORS.onSurfaceVariant,
            }}
          >
            {labels?.empty ?? 'No items in cart'}
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {lines.map((line) => (
              <li key={line.id} style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontSize: 14 }}>
                <span style={{ flex: 1, minWidth: 0 }}>{line.name}</span>
                <span style={{ color: COLORS.onSurfaceVariant }}>×{line.quantity}</span>
                <span style={{ fontWeight: 700 }}>{line.price}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
